/**
 * useDashboardSource — generic fetch-once-on-mount hook for one Dashboard
 * card's data source (WSRPDM-0017). The Dashboard fetches queue summary,
 * schedule config, notifications, and the tenant roster in parallel, each
 * through its own instance of this hook — so a slow or failing source
 * degrades only the card(s) that depend on it, never the whole screen (the
 * spec's central requirement).
 *
 * `fetchFn` is read through a ref, NOT the effect's dependency array — same
 * reasoning as `usePolling.ts`: every call site passes an inline closure
 * (`() => getSummary()`), a fresh function identity on every render, and
 * depending on it directly would refetch forever. Unlike `usePolling`, this
 * fetches exactly once (no interval) — the spec's "a single fetch on mount
 * is acceptable" default.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface DashboardSource<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
  /** Re-run this source's fetch (e.g. a card's own Retry action). */
  refetch: () => void;
}

export function useDashboardSource<T>(fetchFn: () => Promise<T>): DashboardSource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const fetchFnRef = useRef(fetchFn);
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  });

  useEffect(() => {
    let cancelled = false;
    fetchFnRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Event-handler-style state set (not inside the effect body) ahead of
  // bumping reloadToken — mirrors useTenantRoster.ts/useScheduleConfig.ts's
  // refetch() pattern (react-hooks/set-state-in-effect).
  const refetch = useCallback(() => {
    setLoading(true);
    setError(false);
    setReloadToken((n) => n + 1);
  }, []);

  return { data, loading, error, refetch };
}
